import { useCallback, useRef } from 'react';

interface RovingTabIndexOptions {
  activeIndex: number;
  itemCount: number;
  onActiveChange: (index: number) => void;
}

interface RovingTabIndexReturn {
  getItemProps: (index: number) => {
    tabIndex: number;
    onMouseEnter: () => void;
    onFocus: () => void;
    ref: (el: HTMLElement | null) => void;
  };
  containerProps: {
    onKeyDown: (e: React.KeyboardEvent) => void;
  };
}

export function useRovingTabIndex({
  activeIndex,
  itemCount,
  onActiveChange,
}: RovingTabIndexOptions): RovingTabIndexReturn {
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!itemCount) return;

      let nextIdx: number | null = null;

      switch (e.key) {
        case 'ArrowDown':
          nextIdx = (activeIndex + 1) % itemCount;
          break;
        case 'ArrowUp':
          nextIdx = (activeIndex - 1 + itemCount) % itemCount;
          break;
        case 'Home':
          nextIdx = 0;
          break;
        case 'End':
          nextIdx = itemCount - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      onActiveChange(nextIdx);
      itemRefs.current[nextIdx]?.focus();
    },
    [activeIndex, itemCount, onActiveChange],
  );

  const getItemProps = useCallback(
    (index: number) => ({
      tabIndex: index === activeIndex ? 0 : -1,
      onMouseEnter: () => onActiveChange(index),
      onFocus: () => onActiveChange(index),
      ref: (el: HTMLElement | null) => {
        itemRefs.current[index] = el;
      },
    }),
    [activeIndex, onActiveChange],
  );

  return {
    getItemProps,
    containerProps: { onKeyDown: handleKeyDown },
  };
}
